import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip
} from '@material-ui/core'
import {
  ArrowBack,
  ListAlt,
  Build,
  Assessment,
  Delete as DeleteIcon,
  Category as MaterialsIcon,
  Tune as TuneIcon,
  AttachMoney as RatesIcon
} from '@material-ui/icons'
import { Description as DescriptionIcon } from '@material-ui/icons'
import SummaryCards from './SummaryCards'
import SectionBreakdown from './SectionBreakdown'
import ActionItems from './ActionItems'
import RecentActivity from './RecentActivity'
import TaskPanel from './TaskPanel/TaskPanel'
import NotificationBell from './NotificationBell'
import NotificationDrawer from './NotificationDrawer/NotificationDrawer'
import UploadDropzone from './UploadDropzone'
import DocumentsSection from '../Documents/DocumentsSection'
import ModulesSection from './ModulesSection/ModulesSection'
import FollowUpPanel from './FollowUpPanel'
import SubmissionReadinessModal from './SubmissionReadinessModal'
import VendorRequestsPanel from './VendorRequestsPanel'

const statusChipProps = (status) => {
  switch (status) {
    case 'draft':
      return { label: 'Draft', chipStyle: { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' } }
    case 'estimating':
      return { label: 'Estimating', chipStyle: { backgroundColor: '#66bb6a', color: '#fff' } }
    case 'review':
      return { label: 'Review', chipStyle: { backgroundColor: '#ffb74d', color: '#fff' } }
    case 'submitted':
      return { label: 'Submitted', chipStyle: { backgroundColor: 'rgba(255,255,255,0.3)', color: '#fff', border: '1px solid rgba(255,255,255,0.5)' } }
    case 'won':
      return { label: 'Won', chipStyle: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'lost':
      return { label: 'Lost', chipStyle: { backgroundColor: '#f44336', color: '#fff' } }
    default:
      return { label: status || 'Draft', chipStyle: { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' } }
  }
}

const navButtonStyle = {
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.4)',
  textTransform: 'none',
  fontSize: 12
}

const cardPanelStyle = {
  backgroundColor: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  padding: 16
}

const ProjectDashboard = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [dashboard, setDashboard] = React.useState(null)
  const [activity, setActivity] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [buildingTab, setBuildingTab] = React.useState(0)
  const [bidPackages, setBidPackages] = React.useState([])
  const [deleteTarget, setDeleteTarget] = React.useState(null)
  const [readinessOpen, setReadinessOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [notifDrawerOpen, setNotifDrawerOpen] = React.useState(false)
  const [notifCount, setNotifCount] = React.useState(0)
  const uploadRef = React.useRef(null)

  React.useEffect(() => {
    fetchDashboard()
    fetchActivity()
    fetchBidPackages()
  }, [projectId])

  const fetchBidPackages = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/bid-packages`).then(res => {
      setBidPackages(res.data)
    }).catch(err => {
      console.error('Failed to fetch bid packages:', err)
    })
  }

  const handleDeletePackage = () => {
    if (!deleteTarget) return
    setDeleting(true)
    axios.delete(`${props.url}/api/bid-estimation/bid-packages/${deleteTarget._id}`)
      .then(() => {
        setDeleting(false)
        setDeleteTarget(null)
        fetchBidPackages()
      })
      .catch(err => {
        setDeleting(false)
        console.error('Failed to delete bid package:', err)
        alert('Failed to delete: ' + (err.response?.data?.message || err.message))
      })
  }

  const fetchDashboard = () => {
    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/dashboard`).then(res => {
      setDashboard(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const fetchActivity = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/activity`).then(res => {
      setActivity(res.data)
    }).catch(err => {
      console.error('Failed to fetch activity:', err)
    })
  }

  if (loading || !dashboard) {
    return (
      <div style={{ backgroundColor: '#fafafa', minHeight: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  const { project, summary, byBuilding, bySection, vendorQuoteSummary } = dashboard
  const buildings = project?.buildings || []
  const buildingNames = buildings.map(b => b.name || b)
  const selectedBuilding = buildingTab === 0 ? null : buildingNames[buildingTab - 1] || null
  const chipProps = statusChipProps(project?.status)

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100%' }}>
      {/* ── Green Header Bar ────────────────────────────────────────── */}
      <Box style={{
        backgroundColor: '#2e7d32',
        color: '#fff',
        padding: '12px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => history.push('/bid-estimation')}
            style={{ color: '#fff', textTransform: 'none' }}
          >
            Back
          </Button>
          <Typography variant="h6" style={{ fontWeight: 600, color: '#fff' }}>
            {project?.name || 'Project Dashboard'}
          </Typography>
          <Chip
            size="small"
            label={chipProps.label}
            style={chipProps.chipStyle}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="outlined" size="small" startIcon={<ListAlt />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/items`)}>
            Line Items
          </Button>
          <Button variant="outlined" size="small" startIcon={<Build />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/rates`)}>
            Rate Tables
          </Button>
          <Button variant="outlined" size="small" startIcon={<Assessment />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/output`)}>
            Output
          </Button>
          <Button variant="outlined" size="small" style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/estimate-review`)}>
            Estimate Review
          </Button>
          {project?.status === 'review' && (
            <Button variant="contained" size="small"
              style={{ backgroundColor: '#fff', color: '#2e7d32', textTransform: 'none', fontSize: 12 }}
              onClick={() => setReadinessOpen(true)}>
              Submit Estimate
            </Button>
          )}
          {project?.status === 'won' && (
            <Button variant="outlined" size="small" style={navButtonStyle}
              onClick={() => history.push(`/bid-estimation/${projectId}/post-close`)}>
              Post-Close
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<MaterialsIcon />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/materials`)}>
            Materials
          </Button>
          <Button variant="outlined" size="small" startIcon={<DescriptionIcon />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/vendor-quotes`)}>
            Vendor Quotes
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/service-builder`)}
          >
            Service Builder
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/estimating`)}
          >
            Estimating
          </Button>
          <NotificationBell
            onOpen={() => setNotifDrawerOpen(true)}
            unreadCount={notifCount}
            onCountChange={setNotifCount}
          />
          <UploadDropzone
            ref={uploadRef}
            projectId={projectId}
            url={props.url}
            onUploadComplete={(result) => {
              fetchDashboard()
              fetchActivity()
              if (result?.action === 'open_quote_review' && result?.vendorQuote?._id) {
                history.push(`/bid-estimation/${projectId}/vendor-quotes`)
              }
              if (result?.action === 'open_bid_response' && result?.bidPackageId) {
                history.push(`/bid-estimation/${projectId}/bid-response/${result.bidPackageId}`)
              }
            }}
          />
        </div>
      </Box>

      {/* ── Content Area ────────────────────────────────────────────── */}
      <div style={{ padding: 20 }}>
        {/* Project Info Card */}
        {project && (
          <Box style={{ marginBottom: 20 }}>
            <div style={cardPanelStyle}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>Project Info</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">Bid Due</Typography>
                  <Typography variant="body2">
                    {project.bidDueDate ? new Date(project.bidDueDate).toLocaleDateString() : 'No due date set'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">Mode</Typography>
                  <Typography variant="body2">{project.teamMode ? 'Team Project' : 'Solo Project'}</Typography>
                </Grid>
                {project.roles && project.roles.length > 0 && project.roles.map(r => (
                  <Grid item xs={6} sm={3} key={r.role}>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
                      {r.role ? r.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : ''}
                    </Typography>
                    <Typography variant="body2">{r.userName || 'Unassigned'}</Typography>
                  </Grid>
                ))}
              </Grid>
            </div>
          </Box>
        )}

        {/* KPI Summary Cards */}
        <Box style={{ marginBottom: 20 }}>
          <SummaryCards summary={summary} />
        </Box>

        {/* Getting started hint — shown when project has no documents, no bid packages, and no line items */}
        {dashboard && summary && summary.totalItems === 0 && bidPackages.length === 0 && (!project?.documents || project.documents.length === 0) && (
          <Box style={{
            marginBottom: 20,
            backgroundColor: '#e3f2fd',
            borderRadius: 8,
            padding: 16,
            border: '1px solid #bbdefb'
          }}>
            <Typography variant="subtitle2" style={{ color: '#1565c0', marginBottom: 4 }}>
              Getting Started
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Upload plans or a bid response to auto-populate line items, or go to{' '}
              <span
                style={{ color: '#1565c0', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => history.push(`/bid-estimation/${projectId}/items`)}
              >
                Line Items
              </span>
              {' '}to manually build your scope of work.
            </Typography>
          </Box>
        )}

        {/* Modules */}
        <Box style={{ marginBottom: 20 }}>
          <ModulesSection projectId={projectId} project={project} />
        </Box>

        {/* Documents */}
        <Box style={{ marginBottom: 20 }}>
          <DocumentsSection
            projectId={projectId}
            project={project}
            url={props.url}
            onRefresh={fetchDashboard}
            onTriggerUpload={() => uploadRef.current?.triggerUpload()}
          />
        </Box>

        {/* Vendor Requests */}
        <Box style={{ marginBottom: 20 }}>
          <VendorRequestsPanel projectId={projectId} url={props.url} />
        </Box>

        {/* Building Tabs */}
        <Box style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: 20
        }}>
          <Tabs
            value={buildingTab}
            onChange={(e, newVal) => setBuildingTab(newVal)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" />
            {buildingNames.map(name => (
              <Tab key={name} label={name} />
            ))}
          </Tabs>
        </Box>

        {/* Two-Column Grid: Section Breakdown + Sidebar */}
        <Grid container spacing={2}>
          {/* Left: Section Breakdown (2/3) */}
          <Grid item xs={12} md={8}>
            <div style={cardPanelStyle}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                Section Breakdown
              </Typography>
              <SectionBreakdown
                bySection={bySection}
                byBuilding={byBuilding}
                selectedBuilding={selectedBuilding}
              />
            </div>
          </Grid>

          {/* Right: Sidebar (1/3) */}
          <Grid item xs={12} md={4}>
            <ActionItems
              projectId={projectId}
              summary={summary}
              bySection={bySection}
              vendorQuoteSummary={vendorQuoteSummary}
            />
            <div style={{ height: 16 }} />
            <TaskPanel projectId={projectId} />
            <div style={{ height: 16 }} />
            <FollowUpPanel projectId={projectId} project={project} onRefresh={fetchDashboard} />
            <div style={{ height: 16 }} />
            <RecentActivity activity={activity} />
          </Grid>
        </Grid>

        {/* Bid Response Packages */}
        {bidPackages.length > 0 && (
          <Box style={{ marginTop: 20 }}>
            <div style={cardPanelStyle}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                Bid Response Packages
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {bidPackages.map(pkg => (
                  <div key={pkg._id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Chip
                      label={`${pkg.name} (${pkg.status})`}
                      clickable
                      color={pkg.status === 'mapped' ? 'primary' : 'default'}
                      variant={pkg.status === 'parse_failed' ? 'outlined' : 'default'}
                      onClick={() => history.push(`/bid-estimation/${projectId}/bid-response/${pkg._id}`)}
                      onDelete={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(pkg)
                      }}
                      deleteIcon={
                        <Tooltip title='Delete this package'>
                          <DeleteIcon />
                        </Tooltip>
                      }
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Box>
        )}
      </div>

      {/* Notification Drawer */}
      <NotificationDrawer
        open={notifDrawerOpen}
        onClose={() => setNotifDrawerOpen(false)}
        onCountChange={setNotifCount}
      />

      {/* Submission Readiness */}
      <SubmissionReadinessModal
        open={readinessOpen}
        projectId={projectId}
        url={props.url}
        onClose={() => setReadinessOpen(false)}
        onSubmitted={() => { setReadinessOpen(false); fetchDashboard() }}
      />

      {/* Delete Package Confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Delete Bid Package?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete <strong>{deleteTarget && deleteTarget.name}</strong>, all its groups, items, and mappings. The source file will also be removed from S3.
          </DialogContentText>
          <DialogContentText style={{ marginTop: 8 }}>
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeletePackage}
            color='secondary'
            variant='contained'
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated,
  users: state.users || []
})

export default connect(mapStateToProps)(ProjectDashboard)
